import React, { FC, PropsWithChildren, useLayoutEffect } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { SignInPage } from '../common/pages/sign-in-page'
import { LoadbalancerPage } from '../common/pages/loadbalancer-page'
import { LoadBalancerDetailsWidgetPage } from '../common/components/load-balancer-details-widget-page'
import { MeasurementDevicePage } from '../common/pages/measurement-device-page'

import { RootState } from './redux/store'
import SupportPageHeader from './components/PageHeader'
import { DashboardPage } from './pages/dashboard-page'
import { OrganisationPage } from './pages/organisation-page'
import { OrganisationsPage } from './pages/organisations-page'
import { ChargePointsPage } from './pages/charge-points-page'
import { ChargePointPage } from './pages/charge-point-page'
import { PartnersPage } from './pages/partners-page'
import { PartnerPage } from './pages/partner-page'
import { MeasurementDevicesPage } from './pages/measurementdevices-page'
import { UsersPage } from './pages/users-page'
import { ApiCustomersPage } from './pages/api-customers-page'
import { ExternalServersPage } from './pages/external-servers-page'
import FirmwarePage from './pages/firmware-page/FirmwarePage'
import { TransfersPage } from './pages/transfers-page'

const RequireAuth: FC<PropsWithChildren> = ({ children }) => {
    const location = useLocation()
    const token = useSelector((state: RootState) => state.token)
    const allowAccess = useSelector((state: RootState) => state.user.isSuperAdmin)

    return token && allowAccess ? (
        <div>
            <SupportPageHeader className="page-layout-header pt-5 pb-6 xl:pt-4 xl:pb-4" />
            {children}
        </div>
    ) : (
        <Navigate to="/login" state={{ from: location.pathname }} replace />
    )
}

const App = () => {
    const token = useSelector((state: RootState) => state.token)

    const location = useLocation()

    useLayoutEffect(() => {
        // Scroll to the top of the page when the route changes
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    }, [location.pathname])

    return (
        <Routes>
            <Route path="/*" element={<Navigate to={token ? '/dashboard' : '/login'} />} />
            <Route path="login" element={<SignInPage />} />
            <Route
                path="dashboard"
                element={
                    <RequireAuth>
                        <DashboardPage />
                    </RequireAuth>
                }
            />
            <Route
                path="companies"
                element={
                    <RequireAuth>
                        <OrganisationsPage />
                    </RequireAuth>
                }
            />

            <Route
                path="operator-changes"
                element={
                    <RequireAuth>
                        <TransfersPage />
                    </RequireAuth>
                }
            />

            <Route
                path="/company/:companyId"
                element={
                    <RequireAuth>
                        <OrganisationPage />
                    </RequireAuth>
                }
            />
            <Route
                path="chargepoints"
                element={
                    <RequireAuth>
                        <ChargePointsPage />
                    </RequireAuth>
                }
            />
            <Route
                path="chargepoints/:chargePointId"
                element={
                    <RequireAuth>
                        <ChargePointPage />
                    </RequireAuth>
                }
            />

            <Route
                path="partners"
                element={
                    <RequireAuth>
                        <PartnersPage />
                    </RequireAuth>
                }
            />
            <Route
                path="partner/:id"
                element={
                    <RequireAuth>
                        <PartnerPage />
                    </RequireAuth>
                }
            />
            <Route
                path="loadbalancer"
                element={
                    <RequireAuth>
                        <LoadbalancerPage />
                    </RequireAuth>
                }
            />
            <Route
                path="loadbalancer/:id"
                element={
                    <RequireAuth>
                        <LoadBalancerDetailsWidgetPage />
                    </RequireAuth>
                }
            />
            <Route
                path="measurementDevices"
                element={
                    <RequireAuth>
                        <MeasurementDevicesPage />
                    </RequireAuth>
                }
            />
            <Route
                path="measurementDevice/:deviceId"
                element={
                    <RequireAuth>
                        <MeasurementDevicePage />
                    </RequireAuth>
                }
            />
            <Route
                path="users"
                element={
                    <RequireAuth>
                        <UsersPage />
                    </RequireAuth>
                }
            />
            <Route
                path="eapi"
                element={
                    <RequireAuth>
                        <ApiCustomersPage />
                    </RequireAuth>
                }
            />
            <Route
                path="firmware"
                element={
                    <RequireAuth>
                        <FirmwarePage />
                    </RequireAuth>
                }
            />
            <Route
                path="externalservers"
                element={
                    <RequireAuth>
                        <ExternalServersPage />
                    </RequireAuth>
                }
            />
        </Routes>
    )
}

export default App
